import _SessionViewTurnsQuery from "../__generated__/SessionViewTurnsQuery.graphql";
import _SessionViewEventsSubscription from "../__generated__/SessionViewEventsSubscription.graphql";
import { CircleCheck, Mic, PhoneOff, ToggleLeft, ToggleRight } from 'lucide-react';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { graphql, useLazyLoadQuery, useSubscription } from 'react-relay';
import { clsx } from 'clsx';
import { CelebrationView } from './CelebrationView.tsx';
import { DebriefView } from './DebriefView.tsx';
import { MeQuery } from '../components/guards';
import { En } from '../i18n/En';
import { t } from '../i18n/t';
import { TutorAvatar } from '../avatar/TutorAvatar';
import { gestureReducer, keyGesture, type GestureEvent } from '../audio/gesture';
import { useSession } from '../session/context';
import { canHearAgain } from '../session/hearAgain';
import { heeded } from '../session/serverEnd.ts';
import { registerFor } from '../profile/register';
import { awaitsFirstWord, awaitsWarmUp, nextPartial, type Mark } from './transcript.ts';
import { greeted, linesFromTurns, learnerTurnCount, opensThinking, type Line } from './turns.ts';
import { follows } from './autoscroll.ts';
import { sessionFinished } from './finished.ts';
import { createPressCounter, opensCapture, pressesRequest } from './talkPresses.ts';
import { Transcript } from './Transcript.tsx';
import { talkLabel } from './talkButton.ts';
import { topicLabel } from './topics.ts';
import type { guardsMeQuery } from '../__generated__/guardsMeQuery.graphql';
import type { SessionViewTurnsQuery } from '../__generated__/SessionViewTurnsQuery.graphql';
import type { SessionViewEventsSubscription, SessionViewEventsSubscription$data } from '../__generated__/SessionViewEventsSubscription.graphql';
const SessionEvents = (_SessionViewEventsSubscription.hash && _SessionViewEventsSubscription.hash !== "2a27ccad51f224e850e086c65a28ef9e" && console.error("The definition of 'SessionViewEventsSubscription' appears to have changed. Run `relay-compiler` to update the generated files to receive the expected data."), _SessionViewEventsSubscription);

/**
 * What the learner already said, before this screen was mounted.
 *
 * The event bus is a broadcast with no replay (`events.rs`), so a resumed
 * session is the one case where the socket can never tell this component what
 * happened: the rows are the only record. Fetched unconditionally rather than
 * only on resume, because a fresh session answers this with an empty list and
 * one code path is worth one round trip.
 *
 * Read once, never refetched. `Events` mounts only after this resolves, so a
 * turn committed in the gap between the snapshot and the subscription is missed
 * rather than shown twice — the cheaper of the two failures, and only reachable
 * by someone talking in another tab.
 *
 * `tutorReady` rides along for the same reason it is read once: it chooses what
 * the waiting state *says*, not what it does. A stale "not ready" costs a
 * sentence of explanation nobody needed; a stale "ready" costs the ordinary
 * waiting message, which is what this screen showed before the field existed.
 * Neither is worth a subscription. See `awaitsWarmUp`.
 *
 * `me.learner.nativeLanguage` rides along too: the aids panel needs it to ask
 * `Turn.translation` for the right language and to decide whether `translate`
 * is even on offer (`aidsFor`, §6). A learner who edits their profile mid-session
 * sees the change on the next reload, not live — the same staleness this whole
 * query already accepts for `tutorReady`.
 */
/** `sessionStorage`, or `null` where reading the property itself throws. */
function sessionStore(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}
const SessionTurns = (_SessionViewTurnsQuery.hash && _SessionViewTurnsQuery.hash !== "9cf1d69db6924b81e30ed243c20dcfe4" && console.error("The definition of 'SessionViewTurnsQuery' appears to have changed. Run `relay-compiler` to update the generated files to receive the expected data."), _SessionViewTurnsQuery);
type SessionEvent = SessionViewEventsSubscription$data['sessionEvents'];

/**
 * Its own component because `useSubscription` cannot be called conditionally,
 * and subscribing before there is a session means sending an empty id and
 * taking a `BAD_USER_INPUT` on mount.
 */
function Events({
  sessionId,
  onEvent,
  onEnded
}: {
  sessionId: string;
  onEvent: (e: SessionEvent) => void;
  onEnded: (why: string) => void;
}) {
  // Memoised because `useSubscription` re-subscribes whenever the config's
  // identity changes, and the server's broadcast channel does not replay. An
  // inline object would tear the subscription down and rebuild it on every
  // render — including the renders the events themselves cause — so a reply
  // would lose the deltas that arrived in each gap.
  const config = useMemo(() => ({
    subscription: SessionEvents,
    variables: {
      sessionId
    },
    onNext: (data: SessionViewEventsSubscription$data | null | undefined) => {
      if (data) onEvent(data.sessionEvents);
    },
    // The server ends this stream when the audio socket tears down, so
    // without these two the last thing the learner ever hears from the
    // session is silence, under a button still offering to record.
    onError: (e: Error) => onEnded(e.message),
    onCompleted: () => onEnded(t('session-dropped-stream'))
  }), [sessionId, onEvent, onEnded]);
  useSubscription<SessionViewEventsSubscription>(config);
  return null;
}

/**
 * One conversation, at `/session/:sessionId`.
 *
 * It no longer owns the session. `SessionProvider` above it holds the identity,
 * the credentials and the audio machine, so arriving here by starting a
 * conversation and arriving here by reloading the page are the same thing from
 * this component's side: read `credentials`, ask to `resume` if the provider
 * has none yet, render.
 *
 * What stays local is the transcript — lines, error marks, the streamed reply,
 * the reaction cue — and the gesture state. `TutorTextDelta` arrives per token,
 * and putting that stream through the context would re-render every consumer on
 * every token for no one's benefit: nothing outside this component reads it.
 */
export function SessionView() {
  const {
    sessionId
  } = useParams<{
    sessionId: string;
  }>();
  const navigate = useNavigate();
  // Destructured rather than kept as one object: a callback closing over the
  // context value would change identity whenever any field of it did -- a new
  // `ready`, a new `credentials` -- and `Events` tears its subscription down and
  // rebuilds it on a new handler identity, losing whatever arrived in the gap.
  // Every function below is `useCallback`-stable inside the provider.
  const {
    credentials,
    ready,
    dead,
    autoCapturing,
    micError,
    micOpening,
    micAllowed,
    ended,
    closing,
    turnsEnded,
    replayable,
    replaying,
    hearAgain,
    resume,
    end,
    die,
    serverEnded,
    beginUtterance,
    endUtterance,
    setHandsFree: setBridgeHandsFree
  } = useSession();

  // The query the guards above already ran, so Relay serves it from the store.
  const me = useLazyLoadQuery<guardsMeQuery>(MeQuery, {});
  const defaultAudience = me.me?.learner?.defaultCharacter?.audience ?? null;

  // Idempotent per id inside the provider, so this fires the mutation exactly
  // once for a cold load and is a no-op when the start screen already
  // connected this session a navigation ago.
  useEffect(() => {
    if (sessionId) resume(sessionId);
  }, [sessionId, resume]);
  const prior = useLazyLoadQuery<SessionViewTurnsQuery>(SessionTurns, {
    sessionId: sessionId ?? ''
  });
  const replayed = useMemo(() => linesFromTurns(
  // Relay types a nullable scalar as `string | null | undefined`,
  // where `PriorTurn` -- kept free of Relay's generated types on
  // purpose, per its own doc comment -- wants plain `string | null`
  // the way every other optional field on this query is normalised
  // below (`nativeLanguage`, `status`, `character`).
  (prior.session?.turns.nodes ?? []).map(turn => ({
    ...turn,
    translationTarget: turn.translationTarget ?? null
  }))), [prior]);

  // The conversation is over if either thing that knows says so: the local
  // flag `endSession` sets while you are watching, or the session's own status
  // when it ended before this screen mounted. See `sessionFinished` -- on the
  // flag alone, a reload just after "All done" showed a finished conversation
  // as a live one.
  const finished = sessionFinished({
    ended,
    status: prior.session?.status ?? null
  });

  // The running total goes to the server whenever it could be the last
  // chance: the conversation finishes (whichever side ended it), the page
  // hides or closes, the screen goes (kid engagement PRD, F0). The server
  // keeps the larger, so sending again costs nothing.
  const presses = useMemo(() => createPressCounter(sessionId ?? '', sessionStore()), [sessionId]);
  const reportPresses = useCallback(() => {
    if (!sessionId || presses.total() === 0) return;
    void fetch('/graphql', pressesRequest(sessionId, presses.total())).catch(() => {});
  }, [presses, sessionId]);
  useEffect(() => {
    if (finished) reportPresses();
  }, [finished, reportPresses]);
  useEffect(() => {
    const onHide = () => {
      if (document.visibilityState === 'hidden') reportPresses();
    };
    window.addEventListener('pagehide', reportPresses);
    document.addEventListener('visibilitychange', onHide);
    return () => {
      window.removeEventListener('pagehide', reportPresses);
      document.removeEventListener('visibilitychange', onHide);
      reportPresses();
    };
  }, [reportPresses]);
  const character = credentials?.character ?? null;
  // Optimistic, because the answer cannot be heard. The greeting is produced
  // when the socket is accepted, which `SessionProvider` does before this route
  // is even navigated to, and the event bus has no replay (`events.rs`): the
  // `StateChanged` announcing it is broadcast before `Events` below has
  // subscribed, to nobody. Starting at `idle` meant sitting under a button
  // reading "Hold to talk" for a whole model call and then playing audio out of
  // nowhere. See `opensThinking` for why this is the right question.
  const [state, setState] = useState(opensThinking(replayed.lines) ? 'thinking' : 'idle');
  // The most recent reaction and when it arrived. `performance.now()` rather
  // than `Date.now()`: the avatar's clock is a rAF timestamp, which shares
  // that timebase, and mixing the two would expire the hold at a random
  // moment — or never.
  const [cue, setCue] = useState<{
    cue: string;
    at: number;
  } | null>(null);
  // Only what this mount heard. The stored turns are derived above rather than
  // seeded into this state: seeding would need an effect, and an effect that
  // writes what a query returned is the standard way to end up with the
  // transcript twice.
  const [live, setLive] = useState<Line[]>([]);
  // Keyed by turn, because that is what the event names and what a line
  // carries. A flat list would need re-matching on every render.
  const [liveMarks, setLiveMarks] = useState<Record<string, Mark[]>>({});
  const [reply, setReply] = useState('');
  // Latched, because `greeted` alone would disable the button again on every
  // later turn's `thinking`. See `greeted`.
  const [opened, setOpened] = useState(() => !opensThinking(replayed.lines));
  useEffect(() => {
    if (greeted({
      state,
      reply
    })) setOpened(true);
  }, [state, reply]);
  const canTalk = ready && opened && dead === null && !closing;
  // The learner's words while they speak -- a preview the committed line
  // replaces. See `nextPartial` for what clears it.
  const [partial, setPartial] = useState('');
  const [holding, setHolding] = useState(false);
  // Read by `applyGesture` to tell a press that opens a capture from one
  // that closes it, outside the `setHolding` updater, which may run twice.
  const holdingNow = useRef(false);
  useEffect(() => {
    holdingNow.current = holding;
  }, [holding]);
  // Kept as a button through the replay, so focus does not drop to the body
  // between a keyboard tap and the replay's end; the queue ignores that tap.
  const hearAgainOffered = replaying || canHearAgain({
    state,
    ready,
    dead,
    closing,
    ended,
    holding,
    micOpening,
    autoCapturing,
    replayable
  });
  // A press under way when the goodbye is announced has no `up` coming: the
  // button is disabled by then, and a disabled button drops its pointer and
  // key events. `serverEnded` has already stopped the microphone.
  useEffect(() => {
    if (closing) setHolding(false);
  }, [closing]);
  // Spec §6: opt-in, off by default -- push-to-talk is the control this
  // phase is measured against, and a learner who never asks for hands-free
  // must get exactly what they had before.
  const [handsFree, setHandsFree] = useState(false);

  // The escape from the optimism above, and the reason it is allowed to be
  // optimistic at all.
  //
  // The event bus cannot end this wait: the `Idle` a failed greeting publishes
  // travels the same unreplayed bus as the `Thinking` that was missed, and
  // with oMLX unreachable the whole turn is over within milliseconds of the
  // socket being accepted -- long before `Events` above has subscribed. The
  // audio socket has no such window, because the provider holds it from before
  // the greeting begins, and `handle_opening` sends its one terminator on
  // every path out including failure and cancellation (`orchestrator.rs`). A
  // turn the socket says has ended is a turn nobody is waiting for.
  //
  // Only out of `thinking`: `Speaking` is a state of its own, so a turn that
  // is actually being heard is not caught by this, and any `StateChanged` that
  // did arrive is better evidence than a terminator.
  useEffect(() => {
    if (turnsEnded > 0) setState(s => s === 'thinking' ? 'idle' : s);
  }, [turnsEnded]);
  const lines = useMemo(() => [...replayed.lines, ...live], [replayed, live]);
  // The condition the server enqueues a debrief on, restated. `learnerTurnCount`
  // is the shared statement of it -- see `turns.ts`.
  const hasTurns = useMemo(() => learnerTurnCount(lines) > 0, [lines]);
  const marks = useMemo(() => ({
    ...replayed.marks,
    ...liveMarks
  }), [replayed, liveMarks]);

  // §9.3's register, from the character this session is actually using — not
  // from an age band, and not from the learner's current preference.
  const register = registerFor(character?.audience ?? defaultAudience);
  // What `aidsFor` calls `hasNativeLanguage`, and what the translation query
  // asks `Turn.translation` for by name -- see the doc comment on `prior`.
  const nativeLanguage = prior.me?.learner?.nativeLanguage ?? null;
  // §11: the transcript stays available in kid mode behind a toggle. Hidden by
  // default is not the same as absent.
  //
  // `null` until the character arrives, because the register does: reading it
  // at `useState` time would latch the adult default before the session's
  // credentials land, and a child would get the transcript they are meant not
  // to have.
  const [showTranscript, setShowTranscript] = useState<boolean | null>(null);
  // Kid mode only. An adult reaching their own debrief needs no speed bump.
  const transcriptVisible = showTranscript ?? register !== 'kid';
  // Counted from the lines already kept rather than from two more pieces of
  // state. Now that a line says who spoke it, this asks rather than parsing a
  // prefix this component wrote itself. Counts the replayed turns too, so a
  // learner who resumed is not congratulated on half their conversation.
  const spoken = useMemo(() => {
    const mine = lines.filter(l => l.who === 'learner');
    const words = mine.reduce((n, l) => n + l.text.split(/\s+/).filter(Boolean).length, 0);
    return {
      turns: mine.length,
      words
    };
  }, [lines]);

  /**
   * Keep the newest line in view while the learner is at the bottom.
   *
   * A pane of fixed height needs this where a scrolling page did not: a line
   * arriving below the fold used to push the page down under the reader, and
   * now it simply lands out of sight, so the tutor talks off-screen.
   *
   * `following` is a ref rather than state because it is read during the
   * layout effect and changes on every scroll event -- as state it would
   * re-render the conversation once per scrolled pixel. It latches on the
   * learner's own scrolling, not on the scrolls performed here, which is why
   * `follows` is asked again after each one.
   */
  const pane = useRef<HTMLDivElement | null>(null);
  const following = useRef(true);
  const onPaneScroll = useCallback(() => {
    if (pane.current) following.current = follows(pane.current);
  }, []);

  // `useLayoutEffect`, so the jump happens in the same frame the line is
  // painted. In `useEffect` the learner sees one frame of the pane still at
  // its old offset, which reads as a flicker on every token of a streamed
  // reply.
  useLayoutEffect(() => {
    const el = pane.current;
    if (!el || !following.current) return;
    el.scrollTop = el.scrollHeight;
    // `state` because the waiting bubble arrives and leaves on it, and it is a
    // whole bubble's worth of height either way.
  }, [lines, reply, partial, state, transcriptVisible, finished]);

  // Set by `SessionEnded`, and read by `heeded` for what follows it. A ref:
  // it changes no render of its own, and the provider's `ended` lags it by
  // the goodbye's audio. Reset per session, since the route's param can
  // change under a mounted view.
  const serverClosed = useRef(false);
  useEffect(() => {
    serverClosed.current = false;
  }, [sessionId]);
  const onEvent = useCallback((ev: SessionEvent) => {
    if (!heeded(ev.__typename, serverClosed.current)) return;
    setPartial(p => nextPartial(p, ev));
    switch (ev.__typename) {
      case 'StateChanged':
        setState(ev.state.toLowerCase());
        break;
      case 'LearnerTurnCommitted':
        setLive(l => [...l, {
          who: 'learner',
          turnId: ev.turnId,
          text: ev.transcript,
          translationTarget: ev.translationTarget ?? null
        }]);
        setReply('');
        break;
      case 'TutorTurnCommitted':
        // The streamed deltas are cleared by the next learner turn, so without
        // this the tutor's half of the conversation disappears as you talk.
        setLive(l => [...l, {
          who: 'tutor',
          turnId: ev.turnId,
          text: ev.transcript,
          translationTarget: ev.translationTarget ?? null
        }]);
        setReply('');
        break;
      case 'TurnReaction':
        setCue({
          cue: ev.cue,
          at: performance.now()
        });
        break;
      case 'TurnErrors':
        setLiveMarks(m => ({
          ...m,
          [ev.turnId]: ev.errors.map(e => ({
            id: e.id,
            start: e.start,
            end: e.end,
            learnerForm: e.learnerForm,
            targetForm: e.targetForm
          }))
        }));
        break;
      case 'TutorTextDelta':
        setReply(r => r + ev.text);
        break;
      case 'SessionFailed':
        setLive(l => [...l, {
          who: 'error',
          text: t('session-error', {
            reason: ev.reason
          })
        }]);
        // The turn is over, so the half-streamed deltas are never going to be
        // completed or superseded by a `TutorTurnCommitted`. Leaving them on
        // screen shows a truncated sentence as if the tutor had said it.
        setReply('');
        break;
      case 'SessionEnded':
        serverClosed.current = true;
        serverEnded();
        break;
    }
  }, [serverEnded]);

  // Ends the session and stays. Navigating to `/session` here -- which this
  // used to do -- unmounted the screen in the same tick the mutation was sent,
  // so the two things a finished session exists to show, `CelebrationView` in
  // the kid register and `DebriefView` in the adult one, were mounted under
  // `ended` that never got the chance to render. A child tapping "All done"
  // landed straight back on the start screen and never saw their stars.
  const onEndSession = useCallback(() => end(), [end]);

  // The session this button appears under has already ended -- `CelebrationView`
  // renders only once it has -- so this navigates rather than ending it twice.
  // `replace`, so Back does not return to a finished conversation.
  const again = useCallback(() => void navigate('/session', {
    replace: true
  }), [navigate]);

  /// The gesture itself, with no idea what device caused it.
  ///
  /// Split from the pointer handler below so the keyboard can drive the same
  /// transition without inventing a `PointerEvent` to do it — a fake one
  /// carries no `pointerId`, and `setPointerCapture(undefined)` throws.
  const applyGesture = useCallback((event: GestureEvent) => {
    if (opensCapture(register, holdingNow.current, event)) presses.press();
    setHolding(was => {
      const next = gestureReducer(register, {
        capturing: was
      }, event).capturing;
      if (next === was) return was;
      // Only the bridge: it sends END_UTTERANCE once the worklet confirms
      // the last audio is out. See the worklet's `ended` message.
      if (next) beginUtterance();else endUtterance();
      return next;
    });
  }, [register, beginUtterance, endUtterance, presses]);

  /// One handler for both registers; `gestureReducer` owns the difference.
  const onGesture = useCallback((event: GestureEvent) => (e: React.PointerEvent) => {
    // Pointer capture, or a release outside the button never arrives and the
    // microphone stays open until the server's utterance cap.
    if (event === 'down') e.currentTarget.setPointerCapture(e.pointerId);
    applyGesture(event);
  }, [applyGesture]);

  /// §11: the mic control is the only control, so it has to work from the
  /// keyboard in both gestures.
  ///
  /// `preventDefault` on a recognised key for two reasons: Space scrolls the
  /// page, and a `<button>` synthesises a `click` from both Space and Enter,
  /// which would be a third way into a gesture this component already drives
  /// from `keydown`/`keyup`.
  const onKey = useCallback((kind: 'down' | 'up') => (e: React.KeyboardEvent) => {
    const event = keyGesture(e.key, kind, e.repeat);
    // Still swallowed when the filter returns null for a repeat: the key is
    // ours, so its default belongs to us for the whole time it is held.
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') e.preventDefault();
    if (event) applyGesture(event);
  }, [applyGesture]);
  const onToggleHandsFree = useCallback((on: boolean) => {
    setHandsFree(on);
    setBridgeHandsFree(on);
  }, [setBridgeHandsFree]);

  // A refused microphone ends whatever asked for it, the press or hands-free
  // (final review): the microphone is asked for on a press now, not at
  // session start, so a refusal arrives with the button live, and "listening"
  // over no microphone is the lie the label exists to avoid.
  useEffect(() => {
    if (!micError) return;
    setHolding(false);
    setHandsFree(false);
    setBridgeHandsFree(false);
  }, [micError, setBridgeHandsFree]);

  // A route with no id cannot exist under this path, but `useParams` types it
  // optional and the subscription below would send an empty string.
  if (!sessionId) return null;

  // No gutters or max-width of its own: `AppLayout`'s `<main>` supplies both
  // to every route, and a second copy here indented this screen twice as far
  // as its neighbours on a phone.
  //
  // Three rows filling the viewport exactly, not a document that scrolls.
  // `h-full` against `main`'s inner column, so this screen never gives the
  // shell's scroller anything to scroll: the head and the controls are real
  // rows that cannot move, and the conversation between them is the only
  // thing that does. Both used to be `sticky` inside one long page, which
  // works only as long as nothing else claims a stacking order -- and put the
  // controls' backdrop over whatever the page had scrolled beneath it, which
  // is why the debrief could not be read past it.
  //
  // From `lg` the head is a column beside the other two (large-desktop plan,
  // Task 4): the face beside the conversation rather than above it, still a
  // block that cannot scroll, and the transcript held to a reading width.
  return <div className="flex h-full min-h-0 flex-col lg:flex-row lg:justify-center lg:gap-8">
      <Events sessionId={sessionId} onEvent={onEvent} onEnded={die} />
      {/* Title, the way out, and the face: the head of the screen.
          A row rather than a pinned block: the face is the thing the learner
          is talking to, and a long conversation used to push it off the top --
          the transcript grows without bound and everything above it went too,
          leaving a child talking to a wall of their own text. As a row it
          cannot scroll at all, so it needs neither a backdrop to hide lines
          passing under it nor a z-index to win against them.
           One container, not two. The title bar used to be a separate
          non-sticky row carrying `z-30` above a `z-10` sticky avatar, so the
          moment the transcript scrolled the avatar pinned to the top and the
          bar slid across it -- higher z-index, no longer pinned. Sharing the
          avatar's container is what makes that unrepresentable.
           The topic comes from the session's own row rather than local state,
          so it survives a reload -- the state it used to read was never
          assigned and every session called itself "Free chat". */}
      <div className="shrink-0 pt-4 pb-3 lg:w-80 lg:pt-8">
        <div className="flex items-center justify-between gap-2 pb-2">
          <div className="truncate font-semibold">
            {credentials?.storyTitle != null ? <En>{credentials.storyTitle}</En> : credentials?.topic != null ? topicLabel(credentials.topic, character?.displayName) : t('session-free-chat')}
          </div>
          {/* `PhoneOff` in the adult register: the mental model is a live
              voice conversation, and hanging up is the one glyph everybody
              already reads that way. `Square` -- a stop shape -- competed with
              the mic below it, which also stops something.
               A child is not hanging up on anybody. §8.2's tone makes ending a
              conversation a completion rather than a destructive act, so kid
              register gets a check and neutral colour; only the adult button
              carries the destructive red, in the same `text-error
              hover:bg-error/10` shape `AccountSettings` already uses.
               `min-h-(--hit-min)` because the whole point of that token is the
              64px kid hit target, and this button had no hit floor at all. */}
          <button className={clsx('btn btn-ghost btn-sm min-h-(--hit-min) gap-2', register === 'kid' ? 'text-base-content/70' : 'text-error hover:bg-error/10')} disabled={finished} onClick={onEndSession}>
            {register === 'kid' ? <CircleCheck aria-hidden className="size-4" /> : <PhoneOff aria-hidden className="size-4" />}
            {t(register === 'kid' ? 'session-end-kid' : 'session-end')}
          </button>
        </div>
        {character ? <TutorAvatar artKey={character.artKey} displayName={character.displayName} accentHex={character.accentHex} intensity={character.intensity} state={state} cue={cue?.cue ?? null} cueAt={cue?.at ?? 0} onHearAgain={hearAgainOffered ? hearAgain : undefined} replaying={replaying} /> : <p className="text-base-content/60 text-sm" aria-live="polite">
            {t('session-status', {
          state
        })}
          </p>}
      </div>
      {/* The only thing on this screen that scrolls, and the reason the rows
          around it can be still. `min-h-0` because a flex item will not shrink
          below its content without it, and an unshrinkable middle pushes the
          controls off the bottom -- the overflow this exists to contain would
          never happen.
           `overscroll-contain` so flicking past the newest line scrolls this
          pane and stops, rather than handing the gesture to the page behind
          it. Everything the session has to say lives in here, the debrief
          included: it arrives when the conversation ends, and a reader has to
          be able to reach the bottom of it. */}
      <div className="flex min-h-0 flex-1 flex-col lg:max-w-2xl">
        <div ref={pane} onScroll={onPaneScroll} className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
          <div className="flex flex-col gap-4 pb-4">
            {register === 'kid' && <button type="button" className="btn btn-ghost btn-sm min-h-(--hit-min) self-start" aria-expanded={transcriptVisible} onClick={() => setShowTranscript(!transcriptVisible)}>
                {t(transcriptVisible ? 'session-transcript-hide' : 'session-transcript-show')}
              </button>}
            {/* The streamed reply goes inside, not beside: it is the tutor speaking
              and belongs in the tutor's bubble. Hidden with the rest in the kid
              register, which §11 requires of the transcript as a whole -- it used
              to sit outside this gate, so a child who had hidden the transcript
              still watched the tutor's words stream in. */}
            {transcriptVisible && <Transcript lines={lines} marks={marks} register={register} character={character} nativeLanguage={nativeLanguage} learner={prior.me?.learner ?? null} reply={reply}
          // Also cleared on `dead`: the socket carrying preview audio can die
          // mid-utterance while the subscription (and this bubble) lives on.
          partial={finished || dead ? '' : partial} awaiting={awaitsFirstWord({
            state,
            reply,
            dead,
            ended: finished
          })} warming={awaitsWarmUp({
            state,
            reply,
            dead,
            ended: finished,
            tutorReady: prior.tutorReady
          })} />}
            {micError && <div className="alert alert-error" role="alert">
                <span>{t('session-no-mic', {
                reason: micError
              })}</span>
              </div>}
            {micAllowed && register !== 'kid' && <p className="text-base-content/70 text-sm" role="status">
                {t('session-mic-allowed')}
              </p>}
            {dead && <div className="alert alert-error" role="alert">
                <span>{t('session-disconnected-reason', {
                reason: dead
              })}</span>
                {/* A link rather than "reload to start again": `/session` decides
                  what to do next, and for a session that has ended elsewhere that
                  is the start screen rather than this same dead conversation. */}
                {/* A dropped socket on a live conversation comes back to it:
                  `/session` finds it still active and `resume` attaches again.
                  Only a finished one starts over. */}
                <Link className="btn btn-sm min-h-(--hit-min)" to="/session">
                  {t(finished ? 'session-start-again' : 'session-reconnect')}
                </Link>
              </div>}
            {register === 'kid' ?
          // No `DebriefView` here, on purpose, in every state -- not only
          // once `ended`: design §5 makes the debrief adult-register-only
          // by construction, and PRD §8.2 is why -- it is an analytical
          // breakdown of the learner's own errors, and a child must not be
          // shown those. The gate is on the register alone because
          // `DebriefView` is a live subscription: mounted with
          // `ended={false}` during the conversation, it can still receive
          // a `"ready"` or `"failed"` status and render debrief content
          // before the local `ended` state catches up, and gating on
          // `register === "kid" && ended` would have left exactly that
          // window open. `CelebrationView` is the whole of what a
          // kid-register session shows, and only once it has ended.
          // `spoken.turns > 0` as well as `ended`: a session nobody spoke in
          // gets no debrief from the server, and congratulating a child on nought
          // words is the kid-register version of that same empty screen.
          finished && spoken.turns > 0 && <CelebrationView sessionId={sessionId} turns={spoken.turns} words={spoken.words} onAgain={again} /> : <DebriefView sessionId={sessionId} ended={finished} hasTurns={hasTurns} />}
            {finished && <div className="mt-6 flex justify-center">
                <Link className="btn btn-primary btn-lg" to="/session" replace>
                  {t('session-new')}
                </Link>
              </div>}
          </div>
        </div>
        {/* The controls, as the bottom row of the screen rather than a bar
          stuck over the middle of it. Pinned for the same reason as the head:
          the mic is the only control this screen has, and a long transcript
          pushed it off the bottom of the page. As a row it is out of the
          scroller entirely, so it covers nothing and nothing has to be
          scrolled out from under it.
           Rendered only while the conversation is live. Once it has ended both
          buttons are disabled and the debrief is what the reader wants, so the
          row gives its height back to the transcript instead of standing there
          dead. */}
        {!finished && <div className="flex shrink-0 flex-col gap-2 pt-2 pb-4">
            <button disabled={!canTalk} aria-pressed={holding} onPointerDown={onGesture('down')} onPointerUp={onGesture('up')} onPointerCancel={onGesture('cancel')} onKeyDown={onKey('down')} onKeyUp={onKey('up')} onBlur={register === 'kid' ? undefined : () => applyGesture('cancel')} className="btn btn-primary btn-lg min-h-(--hit-min)">
              <Mic aria-hidden className="size-5" />
              {t(talkLabel({
            dead: dead !== null,
            ready,
            opened,
            closing,
            holding,
            opening: micOpening,
            autoCapturing,
            register
          }))}
            </button>
            <label className="label cursor-pointer justify-center gap-2 self-center">
              <input type="checkbox" className="hidden" checked={handsFree} disabled={!canTalk} onChange={e => onToggleHandsFree(e.target.checked)} />
              <div className="flex items-center gap-2 ps-1">
                {handsFree ? <ToggleRight aria-hidden className="size-10 text-primary" /> : <ToggleLeft aria-hidden className="size-10" />}
                <span className="text-sm">
                  {t(register === 'kid' ? 'session-hands-free-kid' : 'session-hands-free')}
                </span>
              </div>
            </label>
          </div>}
      </div>
    </div>;
}